import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Loader } from 'web-ui-components/lib/organisms/modals';
import { TimedProgressContainer } from './containers/TimedProgressContainer';
import { QuestionContainer } from './containers/QuestionContainer';
import { createClient } from './api';
import { validateChallenges } from './utils/validateChallenges';

const invalidChallengeError = {
  id: 1,
  reason: 'Invalid challenge response received. Response was malformed - no questions / answers.'
};

class IdentityVerificationUI extends Component {
  constructor(props) {
    super(props);

    const { accessToken, baseUrl, bankId, caseInfo, clientContext } = props;

    this.onAnswerSelected = this.onAnswerSelected.bind(this);
    this.onTimerExpired = this.onTimerExpired.bind(this);
    this.onScreenshotSubmitted = this.onScreenshotSubmitted.bind(this);
    this.onApiError = this.onApiError.bind(this);

    this.apiClient = createClient(
      {
        accessToken,
        baseUrl,
        bankId,
        caseInfo,
        clientContext
      },
      this.onApiError
    );

    this.state = {
      questions: [],
      answers: [],
      loading: false
    };
  }

  componentDidMount() {
    const { onError } = this.props;
    this.onLoadingStateChanged(true);
    this.apiClient
      .getQuestions()
      .then(({ challenges }) => {
        if (!validateChallenges(challenges)) {
          onError(invalidChallengeError);
        } else {
          this.setState({ questions: challenges });
        }
        this.onLoadingStateChanged(false);
      })
      .catch(error => {
        this.onLoadingStateChanged(false);
        onError(error);
      });
  }

  onAnswerSelected(answer, screenshot) {
    const { answers, questions } = this.state;
    const currentQuestionIndex = this.getCurrentQuestionIndex();
    const newAnswers = answers.concat({
      question_id: questions[currentQuestionIndex].id,
      answer
    });
    this.onLoadingStateChanged(true);
    this.setState({ answers: newAnswers });
    this.apiClient
      .attachEvidence(this.props.caseInfo.screenshotContext, screenshot)
      .then(this.onScreenshotSubmitted);
  }

  onTimerExpired() {
    this.onLoadingStateChanged(true);
    this.submitAnswers();
  }

  onLoadingStateChanged(loadingState) {
    this.setState({ loading: loadingState });
    if (this.props.onLoadingStateChanged) {
      this.props.onLoadingStateChanged(loadingState);
    }
  }

  submitAnswers() {
    this.apiClient
      .submitAnswers(this.state.answers)
      .then(() => this.props.onAnswersSubmitted())
      .then(() => this.onLoadingStateChanged(false));
  }

  onScreenshotSubmitted() {
    if (this.isLastQuestionAnswered()) {
      return this.submitAnswers();
    }
    this.onLoadingStateChanged(false);
  }

  getCurrentQuestionIndex() {
    return Math.min(this.state.answers.length, this.state.questions.length - 1);
  }

  isLastQuestionAnswered() {
    return this.state.answers.length === this.state.questions.length;
  }

  onApiError(error) {
    this.props.onError(error);
    this.onLoadingStateChanged(false);
  }

  render() {
    const { onLoadingStateChanged } = this.props;
    const { questions, loading } = this.state;
    const currentQuestionIndex = this.getCurrentQuestionIndex();

    return (
      <>
        <Loader
          isOpen={!onLoadingStateChanged && loading}
          title={`Just a sec`}
          body={`We’re doing our thing – don’t go anywhere`}
        />
        {questions.length > 0 && (
          <div className="u-margin-top-4">
            <TimedProgressContainer
              minsBeforeTimeout={5}
              currentQuestion={currentQuestionIndex + 1}
              totalQuestions={questions.length}
              timerEnabled={!this.isLastQuestionAnswered()}
              onTimerExpired={this.onTimerExpired}
            />
            <div className="u-margin-top-4">
              <QuestionContainer
                onAnswerSelected={this.onAnswerSelected}
                question={questions[currentQuestionIndex]}
                currentQuestionIndex={currentQuestionIndex + 1}
              />
            </div>
          </div>
        )}
      </>
    );
  }
}

IdentityVerificationUI.propTypes = {
  onAnswersSubmitted: PropTypes.func.isRequired,
  env: PropTypes.shape({
    apiBaseUrl: PropTypes.string.isRequired
  }).isRequired,
  caseInfo: PropTypes.shape({
    type: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    screenshotContext: PropTypes.string.isRequired,
    subType: PropTypes.string
  }).isRequired,
  bankId: PropTypes.string.isRequired,
  accessToken: PropTypes.string.isRequired,
  clientContext: PropTypes.string.isRequired,
  onError: PropTypes.func.isRequired,
  onLoadingStateChanged: PropTypes.func,
  loaderProps: PropTypes.shape({
    title: PropTypes.string,
    body: PropTypes.string
  })
};

export { IdentityVerificationUI };
